import React, { useEffect, useState } from 'react';
import { PanelRight, X } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { useSelector } from 'react-redux';
import { selectTenantLogoSrc, selectTenantSmallLogoSrc, selectTenantLogos } from '../../../../features/tenant-slice';

interface LogoProps {
	collapsed?: boolean;
	onToggleSidebar?: () => void;
	showToggleIcon?: boolean; 
	isLoginPage?: boolean;
}

export const Logo: React.FC<LogoProps> = ({ 
	collapsed = false, 
	onToggleSidebar, 
	showToggleIcon = true,
	isLoginPage 
}) => {
	const [showExpandIcon, setShowExpandIcon] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	
	// Get tenant logos from Redux store
	const tenantLargeLogo = useSelector(selectTenantLogoSrc);
	const tenantSmallLogo = useSelector(selectTenantSmallLogoSrc);
	const tenantInfo = useSelector(selectTenantLogos);

	// Helper function to get logo source with fallback
	const getLogoSrc = (type: 'large' | 'small'): string => {
		if (type === 'large') {
			// Priority: 1. Tenant large logo, 2. Environment large logo
			if (tenantLargeLogo) return tenantLargeLogo;
			return `/logos/${process.env.REACT_APP_LOGO}`;
		} else {
			// Priority: 1. Tenant small logo, 2. Tenant large logo, 3. Environment small logo, 4. Environment large logo
			if (tenantSmallLogo) return tenantSmallLogo;
			if (tenantLargeLogo) return tenantLargeLogo;
			if (process.env.REACT_APP_SMALL_LOGO) return `/logos/${process.env.REACT_APP_SMALL_LOGO}`;
			return `/logos/${process.env.REACT_APP_LOGO}`;
		}
	};

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);
	}, []);

	if (collapsed) {
		return (
			<div
				className="flex items-center justify-center h-20 px-3 relative py-2"
				onMouseEnter={() => showToggleIcon && setShowExpandIcon(true)}
				onMouseLeave={() => showToggleIcon && setShowExpandIcon(false)}
			>
				<img
					src={getLogoSrc('small')}
					alt={tenantInfo?.tenantName ? `${tenantInfo.tenantName} Logo` : 'CX Data Logo'}
					className={`w-[36px] h-[22.32px] object-contain transition-all duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] transform hover:scale-105 select-none pointer-events-none ${
						showToggleIcon && showExpandIcon ? 'opacity-0' : 'opacity-100'
					}`}
					draggable="false"
					style={{
						userSelect: 'none',
						WebkitUserSelect: 'none',
						MozUserSelect: 'none',
						msUserSelect: 'none',
					}}
					onError={(e) => {
						// Fallback to environment logo if tenant logo fails to load
						const target = e.target as HTMLImageElement;
						if (target.src !== `/logos/${process.env.REACT_APP_LOGO}`) {
							target.src = process.env.REACT_APP_SMALL_LOGO 
								? `/logos/${process.env.REACT_APP_SMALL_LOGO}` 
								: `/logos/${process.env.REACT_APP_LOGO}`;
						}
					}}
				/>

				{showToggleIcon && showExpandIcon && (
					<Tooltip text="Open sidebar" position="right">
						<button
							onClick={onToggleSidebar}
							className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-2 hover:bg-tertiary-background rounded-[40px] hover:scale-110 active:scale-95"
							title=""
						>
							{isMobile ? (
								<X className="w-[20px] h-[20px] text-light-grey" strokeWidth={1.75}/>
							) : (
								<PanelRight className="w-[20px] h-[20px] text-[20px] text-light-grey" strokeWidth={1.75} />
							)}
						</button>
					</Tooltip>
				)}
			</div>
		);
	}

	return (
		<div className="flex items-center justify-between h-20 px-4 pt-4 pb-6">
			<img
				src={getLogoSrc('large')}
				alt={tenantInfo?.tenantName ? `${tenantInfo.tenantName} Logo` : 'CK Logo'}
				className={`${isLoginPage ? 'w-[220px] h-[73.92px]' : 'w-[120px] h-[40.32px]' }  object-contain transition-all duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] transform hover:scale-105 select-none pointer-events-none `}
				draggable="false"
				style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
				onError={(e) => {
					// Fallback to environment logo if tenant logo fails to load
					const target = e.target as HTMLImageElement;
					if (target.src !== `/logos/${process.env.REACT_APP_LOGO}`) {
						target.src = `/logos/${process.env.REACT_APP_LOGO}`;
					}
				}}
			/>

			{showToggleIcon && (
				<Tooltip text="Close sidebar" position="right">
					<button
						onClick={onToggleSidebar}
						className="p-2 hover:bg-tertiary-background rounded-[40px] ease-[cubic-bezier(0.4,0,0.2,1)]"
						title=""
					>
						{isMobile ? (
							<X className="w-[20px] h-[20px] text-light-grey" strokeWidth={1.75} />
						) : (
							<PanelRight className="w-[20px] h-[20px] text-light-grey" strokeWidth={1.75} />
						)}
					</button>
				</Tooltip>
			)}
		</div>
	);
};